import { useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { GlossaryStatusBadge } from './GlossaryStatusBadge'
import { useUpdateGlossaryStatus } from '@/api/knowledge'
import type { GlossaryTerm, GlossaryTermStatus } from '@/types/knowledge'

const ALL_STATUSES: GlossaryTermStatus[] = ['Proposed', 'Approved', 'Rejected', 'Deprecated']

interface GlossaryTermDialogProps {
  term: GlossaryTerm | null
  open: boolean
  onOpenChange: (open: boolean) => void
  canEdit: boolean
}

export function GlossaryTermDialog({ term, open, onOpenChange, canEdit }: GlossaryTermDialogProps) {
  const [newStatus, setNewStatus] = useState<GlossaryTermStatus | ''>('')
  const updateStatus = useUpdateGlossaryStatus()

  if (!term) return null

  const handleUpdateStatus = () => {
    if (!newStatus) return
    updateStatus.mutate(
      { termId: term.termId, status: { status: newStatus } },
      {
        onSuccess: () => {
          setNewStatus('')
          onOpenChange(false)
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            {term.canonicalName}
            <GlossaryStatusBadge status={term.status} />
          </DialogTitle>
          <DialogDescription>{term.definition}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2 shrink-0">
          {term.aliases.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-1">Aliases</p>
              <div className="flex flex-wrap gap-1">
                {term.aliases.map((alias) => (
                  <Badge key={alias} variant="outline">{alias}</Badge>
                ))}
              </div>
            </div>
          )}

          {term.language && (
            <div>
              <p className="text-sm font-medium mb-1">Language</p>
              <Badge variant="secondary">{term.language}</Badge>
            </div>
          )}
        </div>

        {term.provenanceDocumentIds.length > 0 && (
          <div className="flex-1 min-h-0 overflow-y-auto pr-2 pt-2">
            <p className="text-sm font-medium mb-1">Source Documents</p>
            <div className="space-y-0.5">
              {term.provenanceDocumentIds.map((docId) => (
                <p key={docId} className="text-xs font-mono text-muted-foreground break-all">{docId}</p>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-4 pt-2 shrink-0">
          <div className="flex gap-6 text-xs text-muted-foreground">
            <span>Created {formatDistanceToNow(new Date(term.createdAt), { addSuffix: true })}</span>
            <span>Updated {formatDistanceToNow(new Date(term.updatedAt), { addSuffix: true })}</span>
          </div>

          {canEdit && (
            <div className="flex items-center gap-2 pt-2 border-t">
              <Select value={newStatus} onValueChange={(v) => setNewStatus(v as GlossaryTermStatus)}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Change status" />
                </SelectTrigger>
                <SelectContent>
                  {ALL_STATUSES.filter((s) => s !== term.status).map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                disabled={!newStatus || updateStatus.isPending}
                onClick={handleUpdateStatus}
              >
                {updateStatus.isPending ? 'Updating...' : 'Update Status'}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
